import { SocialMediaType } from 'api/graphql-global-types';

// Same profile-link rules as fs-web, the mobile app, and fs-backend.
const PROFILE_HOSTS: Record<SocialMediaType, string[]> = {
  [SocialMediaType.Instagram]: ['instagram.com', 'instagr.am'],
  [SocialMediaType.Facebook]: ['facebook.com', 'fb.com', 'fb.me'],
  [SocialMediaType.Twitter]: ['twitter.com', 'x.com'],
  [SocialMediaType.Tiktok]: ['tiktok.com'],
  [SocialMediaType.Youtube]: ['youtube.com', 'youtu.be'],
};

const RESERVED_PATHS: Record<SocialMediaType, string[]> = {
  [SocialMediaType.Instagram]: [
    'p',
    'reel',
    'reels',
    'tv',
    'stories',
    'explore',
  ],
  [SocialMediaType.Facebook]: ['watch', 'groups', 'events', 'marketplace'],
  [SocialMediaType.Twitter]: [
    'i',
    'home',
    'search',
    'hashtag',
    'explore',
    'intent',
  ],
  [SocialMediaType.Tiktok]: [
    'video',
    'tag',
    'discover',
    'music',
    'effect',
    'live',
  ],
  [SocialMediaType.Youtube]: [
    'watch',
    'shorts',
    'playlist',
    'results',
    'feed',
    'embed',
    'hashtag',
  ],
};

const MIN_HANDLE_LENGTH = 2;

const toUrl = (value: string): URL | null => {
  if (!value) {
    return null;
  }

  try {
    return new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return null;
  }
};

const toHandle = (pathname: string): string => {
  try {
    return (
      decodeURIComponent(pathname)
        .split('/')
        .filter(Boolean)[0]
        ?.replace(/^@/, '') ?? ''
    );
  } catch {
    return '';
  }
};

export const isSocialProfileLink = (
  platform: SocialMediaType,
  link?: string | null
): boolean => {
  const hosts = PROFILE_HOSTS[platform];

  if (!hosts || typeof link !== 'string') {
    return false;
  }

  const url = toUrl(link.trim());

  if (!url) {
    return false;
  }

  const host = url.hostname.toLowerCase().replace(/^www\./, '');

  if (!hosts.includes(host)) {
    return false;
  }

  const handle = toHandle(url.pathname);

  return (
    handle.length >= MIN_HANDLE_LENGTH &&
    !RESERVED_PATHS[platform].includes(handle.toLowerCase())
  );
};

export const getSocialProfileLinkExample = (
  platform: SocialMediaType
): string => `https://${PROFILE_HOSTS[platform]?.[0]}/yourname`;

export const getSocialProfileLinkError = (
  platform?: SocialMediaType,
  link?: string | null
): string | undefined => {
  if (!platform || !link?.trim()) {
    return undefined;
  }

  return isSocialProfileLink(platform, link)
    ? undefined
    : `Enter a link to your profile, e.g. ${getSocialProfileLinkExample(
        platform
      )}`;
};
